import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Send as SendIcon,
  Preview as PreviewIcon,
  Info as InfoIcon,
  Code as CodeIcon,
  TextFields as TextIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import MonacoEditor from '@monaco-editor/react';
import { marketingApi } from '../../api/marketing';
import type { MarketAudience, MarketCampaign } from '../../api/marketing';

const steps = ['Select Audience', 'Compose Email', 'Preview & Test', 'Send'];

const DEFAULT_HTML_TEMPLATE = `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.6;
            background-color: #f5f5f5;
            padding: 40px 20px;
        }
        .email-wrapper {
            max-width: 600px;
            margin: 0 auto;
        }
        .email-container {
            background: #ffffff;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
        .header {
            background: linear-gradient(135deg, #e8f4f8 0%, #d9eef5 100%);
            color: #1a365d;
            padding: 40px;
            text-align: center;
        }
        .header h1 {
            margin: 0;
            font-size: 28px;
            font-weight: 700;
            letter-spacing: -0.5px;
            color: #1a365d;
        }
        .content {
            padding: 40px;
            color: #333;
        }
        .greeting {
            font-size: 16px;
            color: #1a365d;
            margin-bottom: 20px;
            font-weight: 500;
        }
        .content p {
            margin: 0 0 16px 0;
            font-size: 15px;
            color: #4a5568;
            line-height: 1.7;
        }
        .highlight-box {
            background: #e8f4f8;
            border-left: 4px solid #0099d8;
            padding: 20px;
            margin: 25px 0;
            border-radius: 4px;
        }
        .highlight-box p {
            margin: 0;
            color: #1a365d;
            font-weight: 500;
        }
        .product-name {
            font-weight: 700;
            color: #0099d8;
        }
        .features-box {
            background: #f7fafc;
            padding: 25px;
            margin: 25px 0;
            border-radius: 6px;
            border: 1px solid #e2e8f0;
        }
        .features-title {
            font-size: 17px;
            font-weight: 600;
            color: #1a365d;
            margin-bottom: 15px;
        }
        .features-box ul {
            list-style: none;
            padding: 0;
            margin: 0;
        }
        .features-box li {
            padding: 8px 0 8px 25px;
            position: relative;
            color: #4a5568;
            font-size: 15px;
        }
        .features-box li::before {
            content: '✓';
            position: absolute;
            left: 0;
            color: #0099d8;
            font-weight: bold;
            font-size: 16px;
        }
        .pricing-box {
            background: #fff;
            border: 2px solid #0099d8;
            border-radius: 8px;
            padding: 25px;
            margin: 25px 0;
            text-align: center;
        }
        .pricing-label {
            font-size: 13px;
            color: #0099d8;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 8px;
        }
        .pricing-amount {
            font-size: 36px;
            font-weight: 700;
            color: #1a365d;
            margin: 8px 0;
        }
        .pricing-note {
            font-size: 14px;
            color: #718096;
            margin-top: 5px;
        }
        .pricing-details {
            margin-top: 15px;
            font-size: 14px;
            color: #4a5568;
            padding-top: 15px;
            border-top: 1px solid #e2e8f0;
        }
        .cta-section {
            text-align: center;
            margin: 35px 0;
        }
        .cta-button {
            background: #0099d8;
            color: white;
            padding: 16px 40px;
            text-decoration: none;
            border-radius: 6px;
            display: inline-block;
            font-weight: 600;
            font-size: 15px;
            transition: background 0.3s;
        }
        .cta-button:hover {
            background: #0077b3;
        }
        .secondary-link {
            display: block;
            margin-top: 15px;
            color: #0099d8;
            text-decoration: none;
            font-weight: 500;
            font-size: 14px;
        }
        .secondary-link:hover {
            text-decoration: underline;
        }
        .signature {
            margin-top: 35px;
            padding-top: 25px;
            border-top: 1px solid #e2e8f0;
            color: #4a5568;
            font-size: 15px;
        }
        .signature-name {
            font-weight: 600;
            color: #1a365d;
        }
        .ps-note {
            background: #f7fafc;
            border-left: 3px solid #cbd5e0;
            padding: 15px 20px;
            margin-top: 25px;
            font-size: 14px;
            color: #718096;
            font-style: italic;
        }
        .footer {
            padding: 25px 40px;
            text-align: center;
            font-size: 13px;
            color: #718096;
            background: #f7fafc;
            border-top: 1px solid #e2e8f0;
        }
        .footer p {
            margin: 6px 0;
        }
        .footer a {
            color: #0099d8;
            text-decoration: none;
        }
        .footer a:hover {
            text-decoration: underline;
        }
        @media only screen and (max-width: 600px) {
            body {
                padding: 20px 10px;
            }
            .header, .content {
                padding: 30px 25px;
            }
            .header h1 {
                font-size: 24px;
            }
            .features-box, .pricing-box {
                padding: 20px;
            }
        }
    </style>
</head>
<body>
    <div class="email-wrapper">
        <div class="email-container">
            <div class="header">
                <h1>SQLFlow is now in VS Code</h1>
            </div>
            <div class="content">
                <p class="greeting">Hi there,</p>

                <p>Since you've been using SQLFlow to visualize data lineage, I thought you'd want to know about something new we just launched.</p>

                <div class="highlight-box">
                    <p><strong class="product-name">Gudu SQL Omni</strong> brings the same column-level lineage analysis you're used to - directly into VS Code.</p>
                </div>

                <div class="features-box">
                    <div class="features-title">What's new:</div>
                    <ul>
                        <li>Right-click any SQL file → instant lineage diagram</li>
                        <li>100% offline - your SQL never leaves your machine</li>
                        <li>Works with 30+ SQL dialects</li>
                        <li>No copy-paste needed - analyze code as you write it</li>
                    </ul>
                </div>

                <p>It's basically SQLFlow, but integrated into your editor workflow.</p>

                <div class="pricing-box">
                    <div class="pricing-label">Launch Week Pricing</div>
                    <div class="pricing-amount">$10/month</div>
                    <div class="pricing-note">(going up soon)</div>
                    <div class="pricing-details">Free version available for up to 10 tables if you want to try it first.</div>
                </div>

                <div class="cta-section">
                    <a href="https://marketplace.visualstudio.com/items?itemName=gudusoftware.gudu-sql-omni" class="cta-button">Install from VS Code Marketplace</a>
                    <a href="https://gudu-sql-omni.gudusoft.com/" class="secondary-link">Learn more →</a>
                </div>

                <p>Thanks for using SQLFlow - hope this makes your workflow even smoother.</p>

                <div class="signature">
                    <p>Best,<br/>
                    <span class="signature-name">James</span><br/>
                    Gudu Software</p>
                </div>

                <div class="ps-note">
                    P.S. - If you have feature requests or feedback, I'd love to hear them. Just reply to this email.
                </div>
            </div>
            <div class="footer">
                <p>You received this email because you're a SQLFlow user.</p>
                <p><a href="{{unsubscribe_url}}">Unsubscribe</a></p>
            </div>
        </div>
    </div>
</body>
</html>`;

const CampaignComposer: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const preselectedAudience = searchParams.get('audience');
  const isEditMode = !!id;

  const [activeStep, setActiveStep] = useState(0);
  const [audiences, setAudiences] = useState<MarketAudience[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [sendTestDialog, setSendTestDialog] = useState(false);
  const [testEmails, setTestEmails] = useState('');
  const [draftCampaignId, setDraftCampaignId] = useState<number | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [emailContent, setEmailContent] = useState('');
  const [loadingCampaign, setLoadingCampaign] = useState(false);

  // Campaign form data
  const [campaignData, setCampaignData] = useState({
    name: 'Gudu SQL Omni Launch - SQLFlow Users',
    subject: 'SQLFlow is now available in VS Code',
    preview_text: 'Gudu SQL Omni brings column-level lineage analysis directly into VS Code',
    from_name: 'James',
    from_email: 'james@gudusoft.com',
    reply_to_email: 'james@gudusoft.com',
    audience: preselectedAudience ? parseInt(preselectedAudience) : 0,
    html: DEFAULT_HTML_TEMPLATE,
    text: '',
    topic: '',
    deduplicate_by_topic: true,
    track_opens: true,
    track_clicks: true,
    send_rate_per_second: 10,
  });

  useEffect(() => {
    fetchAudiences();
  }, []);

  // Load campaign data if editing
  useEffect(() => {
    if (isEditMode && id) {
      loadCampaign(parseInt(id));
    }
  }, [id, isEditMode]);

  // Generate preview when entering step 2 or when HTML/subject changes
  useEffect(() => {
    if (activeStep === 2 && campaignData.html) {
      generatePreview();
    }
  }, [activeStep, campaignData.html, campaignData.subject]);

  const loadCampaign = async (campaignId: number) => {
    try {
      setLoadingCampaign(true);
      const campaign = await marketingApi.getCampaign(campaignId);

      // Populate form with campaign data
      setCampaignData({
        name: campaign.name,
        subject: campaign.subject,
        preview_text: campaign.preview_text,
        from_name: campaign.from_name || '',
        from_email: campaign.from_email || '',
        reply_to_email: campaign.reply_to_email || '',
        audience: campaign.audience,
        html: campaign.html,
        text: campaign.text || '',
        topic: campaign.topic || '',
        deduplicate_by_topic: campaign.deduplicate_by_topic,
        track_opens: campaign.track_opens,
        track_clicks: campaign.track_clicks,
        send_rate_per_second: campaign.send_rate_per_second,
      });

      setDraftCampaignId(campaign.id);
    } catch (error) {
      console.error('Failed to load campaign:', error);
      alert('Failed to load campaign');
      navigate('/marketing/campaigns');
    } finally {
      setLoadingCampaign(false);
    }
  };

  const fetchAudiences = async () => {
    try {
      setLoading(true);
      const response = await marketingApi.getAudiences({ is_active: true });
      setAudiences(response.results);
    } catch (error) {
      console.error('Failed to fetch audiences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    // Save draft when moving from compose to preview
    if (activeStep === 1) {
      await handleSaveDraft(false); // silent save without notification
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const generatePreview = async () => {
    try {
      // First try to use backend preview if we have a draft
      if (draftCampaignId) {
        try {
          const previewData = await marketingApi.previewCampaign(
            draftCampaignId,
            'john@example.com',
            'John Doe'
          );
          console.log('Using backend preview');
          setPreviewHtml(previewData.html);
          return;
        } catch (error) {
          console.log('Backend preview failed, using client-side preview:', error);
        }
      }

      // Fallback to client-side preview
      let preview = campaignData.html || '';

      console.log('Generating client-side preview with HTML length:', preview.length);

      // Only decode if the content appears to be HTML-encoded
      if (preview.includes('&lt;') || preview.includes('&gt;') || preview.includes('&quot;')) {
        const textArea = document.createElement('textarea');
        textArea.innerHTML = preview;
        preview = textArea.value;
      }

      // Replace template variables with sample data
      preview = preview.replace(/\{\{name\}\}/g, 'John Doe');
      preview = preview.replace(/\{\{email\}\}/g, 'john@example.com');
      preview = preview.replace(/\{\{subject\}\}/g, campaignData.subject);
      preview = preview.replace(/\{\{unsubscribe_url\}\}/g, '#unsubscribe');

      console.log('Preview HTML generated, length:', preview.length);
      setPreviewHtml(preview);
    } catch (error) {
      console.error('Failed to generate preview:', error);
      // Fallback to original HTML if preview fails
      setPreviewHtml(campaignData.html || '');
    }
  };

  const handleSendTest = async () => {
    try {
      const emails = testEmails.split(',').map(e => e.trim()).filter(e => e);
      if (emails.length === 0) {
        alert('Please enter at least one email address');
        return;
      }

      // Save/update campaign first
      let campaignId = draftCampaignId;
      if (draftCampaignId) {
        // Update existing campaign
        await marketingApi.updateCampaign(draftCampaignId, {
          ...campaignData,
          status: 'draft' as any,
        });
      } else {
        // Create new campaign
        const campaign = await marketingApi.createCampaign({
          ...campaignData,
          status: 'draft' as any,
        });
        campaignId = campaign.id;
        setDraftCampaignId(campaign.id);
      }

      await marketingApi.sendCampaign(campaignId!, true, emails);
      setSendTestDialog(false);
      setTestEmails('');
      alert('Test emails sent successfully!');
    } catch (error) {
      console.error('Failed to send test:', error);
      alert('Failed to send test emails');
    }
  };

  const handleSaveDraft = async (showNotification = true) => {
    try {
      setLoading(true);

      if (draftCampaignId) {
        // Update existing draft
        await marketingApi.updateCampaign(draftCampaignId, {
          ...campaignData,
          status: 'draft' as any,
        });
      } else {
        // Create new draft
        const campaign = await marketingApi.createCampaign({
          ...campaignData,
          status: 'draft' as any,
        });
        setDraftCampaignId(campaign.id);
      }

      if (showNotification) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Failed to save draft:', error);
      alert('Failed to save draft');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCampaign = async () => {
    try {
      setLoading(true);

      // Save/update campaign first
      let campaignId = draftCampaignId;
      if (draftCampaignId) {
        // Update existing campaign
        await marketingApi.updateCampaign(draftCampaignId, campaignData);
      } else {
        // Create new campaign
        const campaign = await marketingApi.createCampaign(campaignData);
        campaignId = campaign.id;
      }

      // Send campaign
      await marketingApi.sendCampaign(campaignId!);

      navigate('/marketing/campaigns');
    } catch (error) {
      console.error('Failed to send campaign:', error);
      alert('Failed to send campaign');
    } finally {
      setLoading(false);
    }
  };

  const selectedAudience = audiences.find(a => a.id === campaignData.audience);

  const parseMarkdown = (text: string, isInline: boolean = true): string => {
    // Parse markdown to HTML
    let html = text;

    // Bold: **text** or __text__
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

    // Italic: *text* or _text_ (but not in URLs or already processed bold)
    html = html.replace(/(?<!\*)\*([^*\s](?:[^*]*[^*\s])?)\*(?!\*)/g, '<em>$1</em>');
    html = html.replace(/(?<!_)_([^_\s](?:[^_]*[^_\s])?)_(?!_)/g, '<em>$1</em>');

    // Links: [text](url) - use inline link style by default
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #0099d8; text-decoration: underline;">$1</a>');

    // Inline code: `code`
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

    return html;
  };

  const generateHtmlFromContent = () => {
    if (!emailContent.trim()) {
      alert('Please paste some email content first');
      return;
    }

    // Convert newlines to paragraphs
    const paragraphs = emailContent
      .split('\n\n')
      .map(para => para.trim())
      .filter(para => para.length > 0);

    // Build the HTML content body with markdown parsing
    let contentBody = '';
    paragraphs.forEach(para => {
      // Check if this is a list (starts with - or *)
      const lines = para.split('\n');
      const isListBlock = lines.every(line => line.trim().match(/^[-*]\s+/));

      if (isListBlock && lines.length > 1) {
        // This is a list block
        contentBody += `                <ul>\n`;
        lines.forEach(line => {
          const listItem = line.trim().replace(/^[-*]\s+/, '');
          const parsedItem = parseMarkdown(listItem);
          contentBody += `                    <li>${parsedItem}</li>\n`;
        });
        contentBody += `                </ul>\n\n`;
      } else {
        // Regular paragraph
        const parsedPara = parseMarkdown(para.replace(/\n/g, '<br/>'));
        contentBody += `                <p>${parsedPara}</p>\n\n`;
      }
    });

    // Generate full HTML with the template structure
    const generatedHtml = `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.6;
            background-color: #f5f5f5;
            padding: 40px 20px;
        }
        .email-wrapper {
            max-width: 600px;
            margin: 0 auto;
        }
        .email-container {
            background: #ffffff;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
        .header {
            background: linear-gradient(135deg, #e8f4f8 0%, #d9eef5 100%);
            color: #1a365d;
            padding: 40px;
            text-align: center;
        }
        .header h1 {
            margin: 0;
            font-size: 28px;
            font-weight: 700;
            letter-spacing: -0.5px;
            color: #1a365d;
        }
        .content {
            padding: 40px;
            color: #333;
        }
        .greeting {
            font-size: 16px;
            color: #1a365d;
            margin-bottom: 20px;
            font-weight: 500;
        }
        .content p {
            margin: 0 0 16px 0;
            font-size: 15px;
            color: #4a5568;
            line-height: 1.7;
        }
        .content ul {
            margin: 16px 0;
            padding-left: 20px;
        }
        .content li {
            margin: 8px 0;
            font-size: 15px;
            color: #4a5568;
            line-height: 1.7;
        }
        .footer {
            padding: 25px 40px;
            text-align: center;
            font-size: 13px;
            color: #718096;
            background: #f7fafc;
            border-top: 1px solid #e2e8f0;
        }
        .footer p {
            margin: 6px 0;
        }
        .footer a {
            color: #0099d8;
            text-decoration: none;
        }
        .footer a:hover {
            text-decoration: underline;
        }
        @media only screen and (max-width: 600px) {
            body {
                padding: 20px 10px;
            }
            .header, .content {
                padding: 30px 25px;
            }
            .header h1 {
                font-size: 24px;
            }
        }
    </style>
</head>
<body>
    <div class="email-wrapper">
        <div class="email-container">
            <div class="header">
                <h1>${campaignData.subject || 'Email Subject'}</h1>
            </div>
            <div class="content">
                <p class="greeting">Hi {{name}},</p>

${contentBody}
            </div>
            <div class="footer">
                <p>You received this email because you're subscribed to our updates.</p>
                <p><a href="{{unsubscribe_url}}">Unsubscribe</a></p>
            </div>
        </div>
    </div>
</body>
</html>`;

    setCampaignData({ ...campaignData, html: generatedHtml });
  };

  const resetToDefaultHtml = () => {
    if (confirm('Are you sure you want to reset to the default HTML template? This will discard your current HTML content.')) {
      setCampaignData({ ...campaignData, html: DEFAULT_HTML_TEMPLATE });
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Target Audience
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Choose the audience that will receive this campaign
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Audience</InputLabel>
              <Select
                value={campaignData.audience}
                onChange={(e) => setCampaignData({ ...campaignData, audience: e.target.value as number })}
                label="Audience"
                required
              >
                {audiences.map((audience) => (
                  <MenuItem key={audience.id} value={audience.id}>
                    <Box>
                      <Typography variant="body2">{audience.name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {audience.member_count} members ({audience.eligible_count} eligible)
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedAudience && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    {selectedAudience.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    {selectedAudience.description}
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Chip label={`${selectedAudience.member_count} total members`} size="small" />
                    <Chip label={`${selectedAudience.eligible_count} eligible`} size="small" color="primary" />
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Compose Your Email
            </Typography>

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Campaign Name"
                value={campaignData.name}
                onChange={(e) => setCampaignData({ ...campaignData, name: e.target.value })}
                margin="normal"
                required
                helperText="Internal name for this campaign"
              />

              <TextField
                fullWidth
                label="Email Subject"
                value={campaignData.subject}
                onChange={(e) => setCampaignData({ ...campaignData, subject: e.target.value })}
                margin="normal"
                required
                helperText="The subject line recipients will see"
              />

              <TextField
                fullWidth
                label="Preview Text"
                value={campaignData.preview_text}
                onChange={(e) => setCampaignData({ ...campaignData, preview_text: e.target.value })}
                margin="normal"
                helperText="Text that appears after subject in inbox"
              />

              <Box display="flex" gap={2}>
                <TextField
                  fullWidth
                  label="From Name"
                  value={campaignData.from_name}
                  onChange={(e) => setCampaignData({ ...campaignData, from_name: e.target.value })}
                  margin="normal"
                />

                <TextField
                  fullWidth
                  label="From Email"
                  value={campaignData.from_email}
                  onChange={(e) => setCampaignData({ ...campaignData, from_email: e.target.value })}
                  margin="normal"
                  type="email"
                />
              </Box>

              <TextField
                fullWidth
                label="Reply-To Email"
                value={campaignData.reply_to_email}
                onChange={(e) => setCampaignData({ ...campaignData, reply_to_email: e.target.value })}
                margin="normal"
                type="email"
              />

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" gutterBottom>
                Campaign Topic (Optional)
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
                Group related campaigns by topic to prevent sending duplicate content to the same contacts
              </Typography>

              <TextField
                fullWidth
                label="Topic/Group"
                value={campaignData.topic}
                onChange={(e) => setCampaignData({ ...campaignData, topic: e.target.value })}
                margin="normal"
                placeholder="e.g., product_launch_2025, newsletter_jan, feature_announcement"
                helperText="Contacts who received any campaign with this topic will be automatically excluded"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={campaignData.deduplicate_by_topic}
                    onChange={(e) => setCampaignData({ ...campaignData, deduplicate_by_topic: e.target.checked })}
                    disabled={!campaignData.topic}
                  />
                }
                label="Prevent duplicate sends (exclude contacts who already received this topic)"
                sx={{ mt: 1, mb: 2 }}
              />

              {campaignData.topic && campaignData.deduplicate_by_topic && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Contacts who have already received any campaign with the topic "{campaignData.topic}" will be excluded from this send.
                </Alert>
              )}
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Available Template Variables:
              </Typography>
              <Typography variant="caption" component="div">
                • {'{{name}}'} - Recipient's name<br />
                • {'{{email}}'} - Recipient's email<br />
                • {'{{unsubscribe_url}}'} - Unsubscribe link (automatically added)
              </Typography>
            </Alert>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" gutterBottom>
              Email Content (Optional)
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Paste your email content here and click "Generate HTML" to convert it to HTML with Gudu branding
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={8}
              value={emailContent}
              onChange={(e) => setEmailContent(e.target.value)}
              placeholder="Paste your email content here...&#10;&#10;Use double line breaks to separate paragraphs.&#10;&#10;Example:&#10;Since you've been using SQLFlow...&#10;&#10;I thought you'd want to know about something new we just launched."
              sx={{ mb: 2 }}
            />
            <Box display="flex" justifyContent="flex-end" gap={2} mb={3}>
              <Button
                variant="outlined"
                onClick={resetToDefaultHtml}
                startIcon={<RefreshIcon />}
              >
                Reset to Default
              </Button>
              <Button
                variant="contained"
                onClick={generateHtmlFromContent}
                disabled={!emailContent.trim()}
                startIcon={<CodeIcon />}
              >
                Generate HTML from Content
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" gutterBottom>
              HTML Content
            </Typography>
            <Paper variant="outlined" sx={{ mb: 3 }}>
              <MonacoEditor
                height="400px"
                language="html"
                value={campaignData.html}
                onChange={(value) => setCampaignData({ ...campaignData, html: value || '' })}
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  wordWrap: 'on',
                  automaticLayout: true,
                }}
                theme="vs-light"
              />
            </Paper>

            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={campaignData.track_opens}
                    onChange={(e) => setCampaignData({ ...campaignData, track_opens: e.target.checked })}
                  />
                }
                label="Track email opens"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={campaignData.track_clicks}
                    onChange={(e) => setCampaignData({ ...campaignData, track_clicks: e.target.checked })}
                  />
                }
                label="Track link clicks"
              />
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Preview & Test
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Review how your email will appear to recipients
                </Typography>
              </Box>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={generatePreview}
                  sx={{ flexShrink: 0 }}
                >
                  Regenerate Preview
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => setSendTestDialog(true)}
                  sx={{ flexShrink: 0 }}
                >
                  Send Test Email
                </Button>
              </Box>
            </Box>

            {/* Email Client Preview */}
            <Paper
              elevation={2}
              sx={{
                mb: 3,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {/* Email Header (like inbox preview) */}
              <Box
                sx={{
                  p: 2.5,
                  bgcolor: '#fafafa',
                  borderBottom: '1px solid #e0e0e0',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {campaignData.subject || '(No subject)'}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="body2" color="textSecondary">
                    <strong>{campaignData.from_name || 'Your Name'}</strong>
                    {' '}&lt;{campaignData.from_email || 'noreply@example.com'}&gt;
                  </Typography>
                </Box>
                {campaignData.preview_text && (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    {campaignData.preview_text}
                  </Typography>
                )}
              </Box>

              {/* Email Body */}
              <Box
                sx={{
                  bgcolor: '#ffffff',
                  minHeight: 450,
                  maxHeight: 700,
                  position: 'relative',
                }}
              >
                {previewHtml && previewHtml.trim() !== '' ? (
                  <Box
                    component="iframe"
                    srcDoc={previewHtml}
                    sx={{
                      width: '100%',
                      minHeight: 450,
                      maxHeight: 700,
                      border: 'none',
                      display: 'block',
                      backgroundColor: 'white',
                    }}
                    title="Email Preview"
                    sandbox="allow-same-origin"
                  />
                ) : campaignData.html && campaignData.html.trim() !== '' ? (
                  <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={450} gap={2}>
                    <Typography color="textSecondary">Generating preview...</Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={generatePreview}
                      startIcon={<PreviewIcon />}
                    >
                      Generate Preview
                    </Button>
                  </Box>
                ) : (
                  <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={450} gap={2}>
                    <Typography color="textSecondary">No HTML content to preview</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Add HTML content in the previous step to see the preview
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setActiveStep(1)}
                    >
                      Go Back to Edit
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Campaign Summary */}
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Campaign Summary
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="textSecondary">Audience:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedAudience?.name}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="textSecondary">Recipients:</Typography>
                    <Chip
                      label={`${selectedAudience?.eligible_count || 0} eligible contacts`}
                      size="small"
                      color="primary"
                    />
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="textSecondary">Tracking:</Typography>
                    <Box display="flex" gap={0.5}>
                      {campaignData.track_opens && <Chip label="Opens" size="small" variant="outlined" />}
                      {campaignData.track_clicks && <Chip label="Clicks" size="small" variant="outlined" />}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Alert severity="info" icon={<InfoIcon />}>
              <Typography variant="body2">
                This campaign will be sent to <strong>{selectedAudience?.eligible_count || 0}</strong> eligible recipients.
                Test your email before sending to ensure everything looks perfect.
              </Typography>
            </Alert>
          </Box>
        );

      case 3:
        return (
          <Box textAlign="center" py={4}>
            <Typography variant="h5" gutterBottom>
              Ready to Send Campaign
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              Your campaign is ready to be sent to {selectedAudience?.eligible_count || 0} recipients.
            </Typography>

            <Card variant="outlined" sx={{ maxWidth: 500, mx: 'auto', mt: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>Campaign Summary</Typography>
                <Box textAlign="left">
                  <Typography variant="body2">
                    <strong>Name:</strong> {campaignData.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Subject:</strong> {campaignData.subject}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Audience:</strong> {selectedAudience?.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Recipients:</strong> {selectedAudience?.eligible_count || 0}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            <Box mt={4}>
              <Button
                variant="contained"
                size="large"
                startIcon={<SendIcon />}
                onClick={handleSendCampaign}
                disabled={loading}
              >
                Send Campaign Now
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  if (loadingCampaign) {
    return (
      <Box sx={{ flexGrow: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Typography>Loading campaign...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/marketing/campaigns')} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {isEditMode ? 'Edit Campaign' : 'Create Campaign'}
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper sx={{ p: 3 }}>
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSaveSuccess(false)}>
            Draft saved successfully!
          </Alert>
        )}

        {renderStepContent()}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<BackIcon />}
          >
            Back
          </Button>

          <Box display="flex" gap={2}>
            {activeStep < steps.length - 1 && (
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={handleSaveDraft}
                disabled={loading || !campaignData.name || !campaignData.subject}
              >
                Save Draft
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={activeStep < steps.length - 1 ? <NextIcon /> : null}
              disabled={
                (activeStep === 0 && !campaignData.audience) ||
                (activeStep === 1 && (!campaignData.name || !campaignData.subject || !campaignData.html))
              }
            >
              {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Send Test Dialog */}
      <Dialog
        open={sendTestDialog}
        onClose={() => setSendTestDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Send a test version of this email to specific addresses
          </Alert>
          <TextField
            fullWidth
            label="Test Email Addresses"
            value={testEmails}
            onChange={(e) => setTestEmails(e.target.value)}
            helperText="Enter email addresses separated by commas"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendTestDialog(false)}>Cancel</Button>
          <Button onClick={handleSendTest} variant="contained">
            Send Test
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignComposer;